import type { Member } from "@/features/containers/containers.types";
import { resolveMemberDisplayName } from "@/features/containers/containers.utils";
import type { AiMember } from "@shared/types/supabase.types";

/**
 * Resolves the single AI persona targeted by a draft message, or `undefined`.
 *
 * A message can @-mention several personas and fans out to all of them, so a
 * general per-persona menu filter is impossible. But the common case is one
 * persona (e.g. the auto-prefilled `@handle` after a reply): when exactly one
 * member's handle appears in the text we return its id so the slash catalog can
 * be filtered to that persona's allowlist. Zero or multiple mentions → undefined
 * (keep the unfiltered catalog; the server still filters authoritatively).
 */
export function resolveSingleTargetedPersonaId(
  members: AiMember[] | undefined,
  text: string,
): string | undefined {
  const mentioned = (members ?? []).filter(
    (member) => member.ai_persona_handle && text.includes(member.ai_persona_handle),
  );
  return mentioned.length === 1 ? mentioned[0].ai_persona_id : undefined;
}

export type ContentPart =
  | { type: "text"; content: string }
  | { type: "ask-user"; raw: string }
  | { type: "kairo-doc-list"; raw: string };

/**
 * Splits an AI message into display parts: plain markdown text and the
 * special ```ask-user / ```kairo-doc-list widget blocks.
 */
export function splitMessageContent(content: string): ContentPart[] {
  const parts: ContentPart[] = [];
  const regex = /```(ask-user|kairo-doc-list)\n([\s\S]*?)```/g;
  let lastIndex = 0;

  for (const match of content.matchAll(regex)) {
    if (match.index > lastIndex) {
      const text = content.slice(lastIndex, match.index);
      if (text.trim()) parts.push({ type: "text", content: text });
    }
    const kind = match[1] as "ask-user" | "kairo-doc-list";
    parts.push({ type: kind, raw: match[0] });
    lastIndex = match.index + match[0].length;
  }

  const remaining = content.slice(lastIndex);
  if (remaining.trim()) parts.push({ type: "text", content: remaining });

  return parts;
}

export interface AskUserQuestion {
  question: string;
  multiSelect: boolean;
  options: { id: string; label: string; description?: string }[];
}

export interface KairoDocListItem {
  id: string;
  name: string;
  updated_at?: string;
  summary?: string;
  url?: string;
}

export interface KairoDocList {
  title?: string;
  items: KairoDocListItem[];
}

export function parseKairoDocListBlock(content: string): KairoDocList | null {
  const match = content.match(/```kairo-doc-list\n([\s\S]*?)```/);
  if (!match) return null;
  try {
    const parsed = JSON.parse(match[1].trim()) as unknown;
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      !Array.isArray((parsed as Record<string, unknown>).items)
    ) {
      return null;
    }
    const items = (parsed as Record<string, unknown>).items as unknown[];
    const validated: KairoDocListItem[] = [];
    for (const item of items) {
      if (
        typeof item !== "object" ||
        item === null ||
        typeof (item as Record<string, unknown>).id !== "string" ||
        typeof (item as Record<string, unknown>).name !== "string"
      ) {
        return null;
      }
      validated.push(item as KairoDocListItem);
    }
    const title = (parsed as Record<string, unknown>).title;
    return {
      ...(typeof title === "string" ? { title } : {}),
      items: validated,
    };
  } catch {
    return null;
  }
}

export function parseAskUserBlock(content: string): AskUserQuestion | null {
  const match = content.match(/```ask-user\n([\s\S]*?)```/);
  if (!match) return null;
  try {
    const parsed = JSON.parse(match[1].trim()) as unknown;
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      typeof (parsed as Record<string, unknown>).question !== "string" ||
      typeof (parsed as Record<string, unknown>).multiSelect !== "boolean" ||
      !Array.isArray((parsed as Record<string, unknown>).options)
    ) {
      return null;
    }
    const options = (parsed as Record<string, unknown>).options as unknown[];
    if (options.length < 2 || options.length > 10) return null;
    for (const opt of options) {
      if (
        typeof opt !== "object" ||
        opt === null ||
        typeof (opt as Record<string, unknown>).id !== "string" ||
        typeof (opt as Record<string, unknown>).label !== "string"
      ) {
        return null;
      }
    }
    return parsed as AskUserQuestion;
  } catch {
    return null;
  }
}

/**
 * Minimal message shape needed to resolve ask-user answered state — lets the
 * same helpers serve chat messages and document comments (CR-23).
 */
export interface AskUserSourceMessage {
  actor_type: string | null;
  content: string | null;
}

export function isAskUserAnswered(
  aiMessageIndex: number,
  messages: AskUserSourceMessage[],
): boolean {
  return findAskUserAnswer(aiMessageIndex, messages) !== undefined;
}

/** Content of the human answer following an ask-user question, if any. */
export function findAskUserAnswer(
  aiMessageIndex: number,
  messages: AskUserSourceMessage[],
): string | undefined {
  for (let i = aiMessageIndex + 1; i < messages.length; i++) {
    const m = messages[i];
    if (
      (m.actor_type === "user" || m.actor_type === "invited_user") &&
      m.content?.startsWith("<!-- ask-user-answer -->")
    ) {
      return m.content;
    }
  }
  return undefined;
}

export function formatDateSeparator(messageDate: Date): string {
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  if (messageDate.toDateString() === today.toDateString()) return "Today";

  if (messageDate.toDateString() === yesterday.toDateString()) return "Yesterday";

  if (messageDate.getFullYear() === today.getFullYear())
    return messageDate.toLocaleDateString(undefined, {
      weekday: "long",
      month: "long",
      day: "numeric",
    });

  return messageDate.toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export function formatFileSize(size: number) {
  if (size < 1024) return `${size} B`;

  const units = ["KB", "MB", "GB"];
  let value = size / 1024;
  let unitIndex = 0;

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  return `${value.toFixed(value >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

/**
 * A message-room participant, as returned by
 * `chat-api.listMemberNamesForRooms` / grouped from that batch RPC.
 */
export type MessageRoomParticipant = {
  actor_type: "user" | "ai" | "invited_user";
  user_id: string | null;
  display_name: string | null;
};

/**
 * CR-67: the Slack-style title for a message room — its participants' names,
 * excluding the current user, humans before AI, first two then ` +N`.
 *
 * Returns "" when there is no one else to name (e.g. a room of just yourself);
 * callers fall back to their own "Untitled message" default, so this function
 * stays free of user-facing (wuchale-extracted) strings and is fully testable.
 * The `, ` join and the `+N` overflow are structural, not translated.
 */
export function getMessageRoomTitle(
  members: MessageRoomParticipant[],
  currentUserId: string,
): string {
  const isSelf = (m: MessageRoomParticipant) =>
    m.actor_type === "user" && m.user_id === currentUserId;
  const rank = (m: MessageRoomParticipant) => (m.actor_type === "ai" ? 1 : 0);

  const names = members
    .filter((m) => !isSelf(m))
    .map((m) => ({ rank: rank(m), name: m.display_name?.trim() }))
    .filter((m): m is { rank: number; name: string } => Boolean(m.name))
    // Humans (user/invited) before AI, then alphabetical — deterministic
    // regardless of the RPC's UNION row order.
    .sort((a, b) => a.rank - b.rank || a.name.localeCompare(b.name))
    .map((m) => m.name);

  if (names.length === 0) return "";
  if (names.length <= 2) return names.join(", ");
  return `${names.slice(0, 2).join(", ")} +${names.length - 2}`;
}

/**
 * Groups the flat batch member list by room and derives each room's
 * participant title (CR-67). Rooms with no nameable other participant map to
 * "" — callers feed the result to `resolveMessageRoomTitle` (CR-97), which
 * applies the edited-name / "All project members" / untitled fallbacks.
 */
export function groupMessageRoomTitles(
  members: (MessageRoomParticipant & { container_id: string })[],
  currentUserId: string,
): Map<string, string> {
  const byRoom = new Map<string, MessageRoomParticipant[]>();
  for (const m of members) {
    const list = byRoom.get(m.container_id);
    if (list) list.push(m);
    else byRoom.set(m.container_id, [m]);
  }
  const titles = new Map<string, string>();
  for (const [roomId, roomMembers] of byRoom) {
    titles.set(roomId, getMessageRoomTitle(roomMembers, currentUserId));
  }
  return titles;
}

/**
 * Maps a container-members row (from useContainerMembersQuery) to a
 * MessageRoomParticipant, resolving the display name via the shared
 * `resolveMemberDisplayName` (which the RPC's SQL mirrors) so the header title
 * matches the sidebar/list titles. (CR-67)
 */
export function memberRowToParticipant(member: Member): MessageRoomParticipant {
  return {
    actor_type: member.actor_type,
    user_id: member.actor_type === "user" ? member.user_id : null,
    display_name: resolveMemberDisplayName(member),
  };
}

/**
 * CR-97: the labels a message-room title can fall back to. Callers pass the
 * wuchale-extracted strings ("All project members", "Untitled message") so
 * this module stays free of user-facing literals — same rule as
 * `getMessageRoomTitle` above.
 */
export type MessageRoomTitleLabels = {
  allProjectMembers: string;
  untitled: string;
};

/**
 * CR-97: the single title rule for a message room, used by the header, the
 * sidebar, the Messages list and the search palette:
 *
 *   1. the stored `name` when a user typed one (trimmed, non-empty) — an
 *      edited title wins everywhere and no longer follows participants or the
 *      share switch;
 *   2. `labels.allProjectMembers` when the room `is_shared`;
 *   3. the CR-67 participant title (`getMessageRoomTitle`) when non-empty;
 *   4. `labels.untitled`.
 *
 * An empty (or whitespace-only / null) stored name is the "unedited" marker
 * (`create_room` stores '' for message rooms since CR-97 stage 1).
 */
export function resolveMessageRoomTitle(
  room: { name: string | null; is_shared: boolean | null },
  participantTitle: string,
  labels: MessageRoomTitleLabels,
): string {
  const edited = room.name?.trim() ?? "";
  if (edited) return edited;
  return resolveMessageRoomDefaultTitle(room, participantTitle, labels);
}

/**
 * CR-97: rules 2–4 of `resolveMessageRoomTitle` — the title a message room
 * shows while nobody has edited it. The header paints it as the editable
 * title's placeholder, so it needs the default on its own.
 */
export function resolveMessageRoomDefaultTitle(
  room: { is_shared: boolean | null },
  participantTitle: string,
  labels: MessageRoomTitleLabels,
): string {
  if (room.is_shared) return labels.allProjectMembers;
  const derived = participantTitle.trim();
  if (derived) return derived;
  return labels.untitled;
}

/**
 * CR-97: what the header should write after the title input settles, or
 * `null` for "nothing". `TitleInput` fires its debounce on mount and after
 * every server refetch — not only on keystrokes — and for a message room an
 * EMPTY value is a valid write (clear = back to the default). So only a value
 * the user actually typed (`typed`) may reach the rename RPC, and only when it
 * differs from what is stored; otherwise opening a room with an edited title
 * would silently clear it. Kept pure so the one data-destroying decision in
 * the header is unit-tested.
 */
export function nextMessageRoomName(input: {
  typed: boolean;
  next: string;
  stored: string | null;
}): string | null {
  if (!input.typed) return null;
  const trimmed = input.next.trim();
  return trimmed === (input.stored ?? "") ? null : trimmed;
}

/**
 * CR-97: label of a room result in the workspace search palette. Search has no
 * participant list, so a message room resolves with an empty participant
 * title; AI chats keep their stored name verbatim (they have no derived
 * default), matching what the search rendered before.
 */
export function resolveSearchRoomLabel(
  room: {
    name: string | null;
    is_shared: boolean | null;
    container_type: "document" | "room" | "message_room";
  },
  labels: MessageRoomTitleLabels,
): string {
  if (room.container_type !== "message_room") return room.name ?? "";
  return resolveMessageRoomTitle(room, "", labels);
}

/**
 * CR-97: the room part of a message result's context line in the search
 * palette. The stored name wins; a shared unedited message room reads "All
 * project members"; otherwise the generic per-type label ("Message" / "Chat")
 * — a blank stored name must never render as an empty context.
 */
export function resolveSearchMessageContext(
  msg: {
    room_name: string | null;
    room_is_shared: boolean | null;
    room_container_type: "room" | "message_room" | null;
  },
  labels: MessageRoomTitleLabels & { message: string; chat: string },
): string {
  if (msg.room_container_type === "message_room") {
    return resolveMessageRoomTitle({ name: msg.room_name, is_shared: msg.room_is_shared }, "", {
      allProjectMembers: labels.allProjectMembers,
      untitled: labels.message,
    });
  }
  return msg.room_name?.trim() || labels.chat;
}

/**
 * CR-107: the container whose human members are the @mention candidates of a
 * chat / message room.
 *
 * A room shared with the project (`containers.is_shared`) has NO
 * `container_members` rows for its audience — access is granted by
 * `can_access_container()` through the parent project — so reading the room's
 * own rows lists only its creator ("No members available" for the creator
 * themself). When the room is shared the humans to offer are therefore the
 * parent project's members; when it is not, the explicit room rows ARE the
 * whole audience. Mirrors the doc editor's `docAccessMembers` rule.
 *
 * The project source is never broader than the room's audience: everyone who
 * can open a shared room is a member of its parent project.
 */
export function resolveMentionMembersContainerId({
  roomId,
  projectContainerId,
  isShared,
}: {
  roomId: string;
  projectContainerId: string;
  isShared: boolean;
}): string {
  return isShared ? projectContainerId : roomId;
}

/**
 * The room fields the CR-106 activity stamp reads and rewrites — the subset of
 * `ContainerItem` that the shared list's "Last updated" sort looks at.
 */
type RoomActivityRow = {
  id: string;
  created_at?: string | null;
  updated_at?: string | null;
};

/**
 * CR-106 stage 2: stamp AI-chat / message rooms with their last activity.
 *
 * Nothing on the message path bumps a room's `containers.updated_at` — the
 * client, the AI orchestrator and comment threads all insert straight into
 * `messages`, and no trigger touches `containers`. So the shared list's "Last
 * updated" sort would order rooms by the last time the container row itself
 * changed, while every card displays the last message's time next to it.
 *
 * Both room list pages already fetch the last message per room for that card,
 * so the activity timestamp is on the client: hand the list rooms whose
 * `updated_at` is `max(room.updated_at ?? room.created_at, lastMessage.created_at)`.
 * Timestamps are compared as strings like `filterAndSortItems` sorts them —
 * code-unit order, which is chronological for the ISO-8601 UTC values Postgres
 * emits and avoids ICU's collation of `.` before `+`. A room whose own timestamp already wins, or
 * that has no message, is returned untouched, so a `null` `updated_at` keeps
 * falling back to `created_at`. Nothing else on the row is changed — the
 * CR-97 derived titles resolved upstream survive.
 */
export function withLastActivity<T extends RoomActivityRow>(
  rooms: T[] | undefined,
  lastMessageByRoom: Map<string, { created_at?: string | null }>,
): T[] | undefined {
  if (!rooms) return rooms;
  return rooms.map((room) => {
    const activity = lastMessageByRoom.get(room.id)?.created_at;
    if (!activity) return room;
    const current = room.updated_at ?? room.created_at ?? "";
    return activity > current ? { ...room, updated_at: activity } : room;
  });
}

/**
 * CR-149: should the chat header render the linked-CR split control?
 *
 * Four independent reasons not to, kept in one place because the header's JSX
 * cannot be unit-tested: the project has Change Requests turned off; the room
 * is a message room, which cannot carry a CR link at all; the chat has no CR
 * (an unlinked chat keeps its previous behaviour — no control — because a link
 * is created from the CR, not from the chat); or the route params needed to
 * build the CR link are missing.
 */
export function shouldShowLinkedCrControl(input: {
  linkedCrId: string | null;
  isAiChat: boolean;
  changeRequestsEnabled: boolean;
  workspaceSlug?: string;
  projectSlug?: string;
}): boolean {
  return (
    !!input.linkedCrId &&
    input.isAiChat &&
    input.changeRequestsEnabled &&
    !!input.workspaceSlug &&
    !!input.projectSlug
  );
}
